import { z } from 'zod';

// Validate the fields consumed by the reused profile/people/document components.
// Pass through unrelated fields so the existing response types remain usable.
const text = z.string().nullish();
const number = z.number().nullish();
const strings = z
  .array(z.string())
  .nullish()
  .transform((items) => items ?? []);
const object = z.object({}).passthrough();
const array = <T extends z.ZodType>(schema: T) =>
  z
    .array(schema)
    .nullish()
    .transform((items) => items ?? []);
const company = z
  .object({
    name: z.string(),
    eu_id: text,
    sitz: text,
    city: text,
    register_id: text,
    register_type: text,
    register_court: text,
    country_code: text,
    lei_code: text,
    favicon_url: text,
    logo_url: text,
  })
  .passthrough();
const participant = z
  .object({
    role: text,
    role_code: text,
    title: text,
    first_name: text,
    middle_name: text,
    last_name: text,
    organization: text,
    city: text,
    date_of_birth: text,
    age: number,
    eu_id: text,
    register_id: text,
    register_type: text,
    register_court: text,
  })
  .passthrough();
const event = z
  .object({
    entry_date: text,
    entry_number: number,
    data: z
      .object({
        member_type: text,
        vorname: text,
        name: text,
        ort: text,
        birthdate: text,
        anteil_percent: number,
        einlage: number,
        total_einlage: number,
        gruendungsdokument: text,
        gruendungsdokument_datum: text,
        erste_eintragung_datum: text,
      })
      .passthrough(),
  })
  .passthrough();
const history = z
  .object({
    kommanditisten: array(event),
    erste_eintragung: event.nullish(),
  })
  .passthrough()
  .nullish();
const insolvency = z
  .object({
    status: z
      .enum(['active', 'dissolved', 'in_liquidation', 'insolvent'])
      .nullish(),
    company_status: text,
    dissolved: z.boolean().nullish(),
    records: array(
      z
        .object({ category: z.string(), publish_date: z.string() })
        .passthrough(),
    ),
  })
  .passthrough()
  .nullish();
const metrics = array(
  z
    .object({
      year: z.number(),
      profit: number,
      total_revenue: number,
      total_balance_sum: number,
      passiva: number,
      size_class: text,
    })
    .passthrough(),
);
const employees = array(
  z.object({ year: z.number(), total: number }).passthrough(),
);
const registerFile = z
  .object({
    file_type: z.string(),
    public_name: text,
    published_at: text,
    doc_date: text,
    fetched_at: text,
  })
  .passthrough();
const registerDocumentFields = {
  register_documents: array(
    z
      .object({
        id: z.number(),
        file_type: z.string(),
        doc_date: text,
        published_at: text,
        stored: z.boolean(),
        fetched_at: text,
      })
      .passthrough(),
  ),
  register_documents_complete: strings,
};
const financialFields = {
  metrics,
  employee_data: employees,
  subsidiaries: array(company),
  ai_summary: text,
  ai_summary_en: text,
  ai_outlook: text,
  ai_outlook_en: text,
  risk_keywords: strings,
  opportunity_keywords: strings,
  products: strings,
  business_branches: array(
    z
      .object({
        name: z.string(),
        revenue: number,
        revenue_unit: text,
        percentage_of_total: number,
        parent_name: text,
      })
      .passthrough(),
  ),
  guv_data: array(object),
  financial_files: array(
    z
      .object({
        id: z.number(),
        file_id: number,
        file_type: z.string(),
        public_name: text,
        year: number,
        bilanz_type: text,
        published_at: text,
        obj_storage_key: text,
        has_file: z.boolean().nullish(),
      })
      .passthrough(),
  ),
};
const registerFields = {
  street: text,
  house_no: text,
  postal_code: text,
  city: text,
  sitz: text,
  gegenstand: text,
  rechtsform_code: number,
  gruendungsdokument: text,
  gruendungsdokument_datum: text,
  erste_eintragung_datum: text,
  participants: array(participant),
  nace_codes: array(
    z
      .object({
        code: z.string(),
        description: text,
        description_en: text,
        section: text,
      })
      .passthrough(),
  ),
  entries: array(object),
  register_files: array(registerFile),
  ...registerDocumentFields,
  company_history: history,
  insolvency,
};

export const previewBasicSchema = z
  .object({
    eu_id: z.string().min(1),
    display_name: z.string().min(1),
    register_type: text,
    register_id: text,
    register_court: text,
    register_city: text,
    address_line: text,
    favicon_url: text,
    logo_url: text,
    updated_at: text,
    is_deleted: z.boolean().nullish(),
    has_gesellschafterliste: z.boolean().nullish(),
    overrides: z
      .object({
        about_us: text,
        street: text,
        house_no: text,
        postal_code: text,
        city: text,
      })
      .passthrough()
      .nullish(),
    lei_parent: company.nullish(),
    lei_children: array(company.extend({ relationship: z.string() })),
    website_metadata: z
      .object({
        homepage_summary_de: text,
        homepage_summary_en: text,
        captured_at: text,
      })
      .passthrough()
      .nullish(),
    founder_profile: z
      .object({
        family_managed: z.boolean().nullish(),
        succession_risk: z.boolean().nullish(),
      })
      .passthrough()
      .nullish(),
    ...registerFields,
    ...financialFields,
  })
  .passthrough();
export const previewRegisterSchema = z
  .object({ eu_id: z.string().min(1), ...registerFields })
  .passthrough();
export const previewContactSchema = z
  .object({
    eu_id: z.string().min(1),
    favicon_url: text,
    enrichment: z.object({ logo_url: text }).passthrough().nullish(),
  })
  .passthrough();
export const previewGesellschafterSchema = z
  .object({
    eu_id: z.string().min(1),
    has_gesellschafterliste: z.boolean(),
    dk_register_file: registerFile.nullish(),
    ...registerDocumentFields,
    gesellschafter: array(
      company.extend({
        type: text,
        title: text,
        first_name: text,
        middle_name: text,
        last_name: text,
        date_of_birth: text,
        shares: number,
        share_percent: number,
        total_shares: number,
        gesellschafter_eu_id: text,
      }),
    ),
  })
  .passthrough();
export const previewFinancialsSchema = z
  .object({
    eu_id: z.string().min(1),
    ...financialFields,
    parent_company: company.nullish(),
  })
  .passthrough();
export const previewNetworkSchema = z
  .object({
    eu_id: z.string().min(1),
    nodes: z.array(
      z
        .object({
          id: z.string().min(1),
          node_type: z.string(),
          label: z.string(),
        })
        .passthrough(),
    ),
    edges: z.array(
      z
        .object({
          source: z.string().min(1),
          target: z.string().min(1),
          role: z.string(),
          source_type: z.string(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export const previewEnvelopeSchema = z.object({ data: z.unknown() });
